import React from "react";

/**
 * The landing page's opening frame, and only that.
 *
 * This deliberately isn't shared with the interior pages: each of those has its
 * own character — a dossier index, a portrait wall, journal front matter, a
 * split-screen invitation — and running them all through one component was what
 * made the site feel like five copies of the same template.
 *
 * The negative top margin pulls the frame up under the navbar, which goes
 * transparent over this route. The bar is `sticky`, so it still occupies its
 * 64/72px of flow; without this the "full-bleed" image would start below it.
 */
export const HomeHero = ({ eyebrow, meta, title, lede, actions, image, imageSrcSet, imageAlt, children }) => (
  <section
    className="relative isolate overflow-hidden flex flex-col justify-end min-h-[92vh] -mt-[64px] sm:-mt-[72px]"
    data-testid="hero-section"
  >
    <img
      src={image}
      srcSet={imageSrcSet}
      // Full-bleed, so it is always the viewport's width.
      sizes="100vw"
      alt={imageAlt}
      // Largest thing above the fold on the site's entry page, so it is fetched
      // eagerly and prioritised rather than lazied like everything below.
      fetchpriority="high"
      decoding="async"
      className="absolute inset-0 -z-10 w-full h-full object-cover grayscale-[30%] contrast-[0.95] hero-pan"
    />
    {/* Three layers, each doing a different job: a flat tint that only applies
        on narrow screens, where the horizontal wash runs out before the line
        does; the wash itself, which carries the type on wide screens; and a lift
        from the bottom so the stat strip stays legible over the brightest part
        of the field. */}
    <div className="absolute inset-0 -z-10 bg-[#1C2722]/45 md:bg-transparent" />
    <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#1C2722] via-[#1C2722]/85 to-[#1C2722]/25" />
    <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#1C2722]/95 via-transparent to-[#1C2722]/40" />

    <div className="max-w-[1400px] w-full mx-auto px-6 lg:px-10 pt-[calc(64px+4rem)] sm:pt-[calc(72px+4rem)] pb-8">
      {(eyebrow || meta) && (
        <div className="flex flex-wrap items-center gap-3 mb-6 fade-up">
          {eyebrow && <span className="overline !text-[#D1CEC7]">{eyebrow}</span>}
          {meta}
        </div>
      )}

      <h1 className="font-serif text-[clamp(2.3rem,5.4vw,4.5rem)] leading-[0.98] tracking-tight text-[#F9F8F6] max-w-[19ch] fade-up d-1">
        {title}
      </h1>

      {lede && (
        <p className="mt-8 text-[17px] leading-relaxed text-[#D1CEC7] max-w-xl fade-up d-2">{lede}</p>
      )}

      {actions && <div className="mt-8 flex flex-wrap gap-4 fade-up d-3">{actions}</div>}
    </div>

    {children}
  </section>
);

/**
 * Button styling for the dark hero ground, as class strings rather than
 * components: the callers need react-router <Link>, not an <a>, and wrapping
 * Link just to set a className buys nothing.
 */
export const heroBtnFilled =
  "inline-flex items-center gap-2.5 bg-[#F9F8F6] text-[#1C2722] border border-[#F9F8F6] px-[22px] py-[14px] font-mono text-[12px] tracking-[0.18em] uppercase transition-colors duration-200 hover:bg-[#A17E3B] hover:border-[#A17E3B] hover:text-[#F9F8F6]";

export const heroBtnOutline =
  "inline-flex items-center gap-2.5 border border-white/40 text-[#F9F8F6] px-[22px] py-[13px] font-mono text-[12px] tracking-[0.18em] uppercase transition-colors duration-200 hover:bg-[#F9F8F6] hover:text-[#1C2722] hover:border-[#F9F8F6]";

export default HomeHero;
